// @ts-nocheck
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom"; // Import useNavigate
import CustomNotification from "../Games/DefaultLottery/components/CustomNotification";
import {postCreateTicketService} from "../services/postCreateTicket.service";

const useCreateTicket = () => {
  const navigate = useNavigate(); // Initialize useNavigate
  const queryClient = useQueryClient();

  const {t} = useTranslation();

  const {
    data,
    isError,
    isPending,
    error,
    mutate: postCreateTicket,
    isSuccess,
  } = useMutation({
    mutationKey: ["create-lottery-ticket"],
    mutationFn: postCreateTicketService,
    onSuccess: response => {
      // Extract the data from the response
      const data = response?.data?.data || response?.data || response;
      const rejectedBets = data?.rejected_bets;
      const _totalRequested = data?.total_requested;
      const _totalAccepted = data?.total_accepted;

      // Check if there are any rejected bets
      const hasRejectedBets = rejectedBets && Array.isArray(rejectedBets) && rejectedBets.length > 0;

      if (hasRejectedBets) {
        // Show warning for partial acceptance
        CustomNotification.warning({
          message: "Algunas apuestas no fueron aceptadas",
          action: {
            text: "Ver mis jugadas",
            onClick: () => navigate("/lottery/tickets"),
          },
          duration: 0, // Requires manual dismissal
        });

        // Show individual notification for each rejected bet with delay
        rejectedBets.forEach((bet, index) => {
          setTimeout(
            () => {
              // Determine identifier: use number if available, otherwise use sign
              const identifier = bet.number || bet.sign_id || `Jugada ${index + 1}`;

              CustomNotification.warning({
                message: `El número ${identifier} no tiene cupo de venta, por lo que no fue procesado en la venta del ticket`,
                duration: 0,
              });
            },
            (index + 1) * 800
          ); // 800ms delay between each notification
        });
      } else {
        // All bets accepted - show success
        CustomNotification.success({
          message: t("lotteryTicketCreatedMessage"),
          action: {
            text: "Ver mis jugadas",
            onClick: () => navigate("/lottery/tickets"),
          },
        });
      }

      queryClient.invalidateQueries(["lottery_tickets"]);
      // Don't navigate automatically since the user can choose to click the button
    },
    onError: error => {
      // Extract backend error data
      const errorData = error?.response?.data || error?.data;
      const backendMessage = errorData?.message || error?.message;
      const figures = errorData?.figures;
      const items = errorData?.items; // For number-based games (Triple, Terminal)
      const status = error?.response?.status || error?.status;

      // Handle limit exceeded errors with figure-specific or item-specific messages
      const limitData = figures || items; // Check both figures and items

      if (status === 400 && limitData && typeof limitData === "object") {
        // Show main error message first
        if (backendMessage) {
          CustomNotification.error({
            message: backendMessage,
            duration: 0, // Requires manual dismissal
          });
        }

        // Show individual error for each figure/item with a delay
        const itemNumbers = Object.keys(limitData);

        itemNumbers.forEach((itemNumber, index) => {
          const itemError = limitData[itemNumber];

          setTimeout(
            () => {
              // Use different label based on whether it's figures or items
              const label = figures ? `Figura ${itemNumber}` : `Número ${itemNumber}`;
              const message = `${label}: ${itemError}`;
              CustomNotification.error({
                message,
                duration: 0, // Requires manual dismissal
              });
            },
            (index + 1) * 1000
          ); // 1000ms delay between each notification
        });
      } else if (status === 500) {
        CustomNotification.error({
          message: "Error del servidor. Por favor intenta nuevamente.",
          duration: 0,
        });
      } else if (status === 401) {
        CustomNotification.error({
          message: "Sesión expirada. Por favor inicia sesión nuevamente.",
          duration: 0,
        });
      } else if (backendMessage) {
        // Use any other backend message if available
        CustomNotification.error({
          message: backendMessage,
          duration: 0,
        });
      } else {
        CustomNotification.error({
          message: t("lotteryTicketCreatedMessageError"),
          duration: 0,
        });
      }
    },
  });

  return {
    postCreateTicket,
    data,
    isError,
    isPending,
    isSuccess,
    error,
  };
};

export default useCreateTicket;
